import React, { useState, useEffect } from 'react';
import { 
  FaServer, 
  FaMemory, 
  FaHdd, 
  FaMicrochip, 
  FaSync, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaExclamationCircle,
  FaNetworkWired,
  FaHeart,
  FaDesktop,
  FaCogs,
  FaChartLine,
  FaExchangeAlt,
  FaClock,
  FaDatabase
} from 'react-icons/fa';
import '../css/ServerInfo.css'; // CSS customizado
import config from '../config/config';
import axios from 'axios';

const ServerInfo = () => {
  const [serverData, setServerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;

  const fetchServerInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await axios.get(`${API_BASE_URL}/api/server/info`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Com axios, os dados estão em response.data
      const result = response.data;
      
      if (result.success) {
        setServerData(result.data);
        setLastUpdate(new Date());
      } else {
        throw new Error(result.message || 'Erro ao carregar dados');
      }
    } catch (error) {
      console.error('Erro ao buscar informações do servidor:', error);
      setError(error.message);
      // Dados mock para desenvolvimento
      //setServerData(getMockData());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServerInfo();
    const interval = setInterval(fetchServerInfo, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'running':
      case 'healthy':
      case 'connected': 
        return '#28a745';
      case 'warning':
        return '#ffc107';
      case 'error':
      case 'unhealthy':
        return '#dc3545';
      default: 
        return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'running':
      case 'healthy':
      case 'connected':
        return <FaCheckCircle color="#28a745" />;
      case 'warning':
        return <FaExclamationTriangle color="#ffc107" />;
      case 'error':
      case 'unhealthy':
        return <FaExclamationCircle color="#dc3545" />;
      default:
        return <FaExclamationCircle color="#6c757d" />;
    }
  };

  const getProgressColor = (value) => {
    if (value > 90) return '#dc3545';
    if (value > 80) return '#ffc107';
    return '#28a745';
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getThreadUsageColor = (usage) => {
    if (usage > 90) return '#dc3545';
    if (usage > 70) return '#ffc107';
    return '#28a745';
  };

  if (loading && !serverData) {
    return (
      <div className="server-info-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Carregando informações do servidor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="server-info-container">
      {/* Cabeçalho */}
      <div className="server-header">
        <h1>Informações do Servidor</h1>
        <div className="header-actions">
          {lastUpdate && (
            <span className="last-update">
              Última atualização: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <button 
            className="refresh-btn"
            onClick={fetchServerInfo} 
            disabled={loading}
          >
            <FaSync className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="alert warning">
          {error} - Mostrando dados de demonstração
        </div>
      )}

      {/* Sistema e Recursos */}
      <div className="resources-grid">
        {/* Informações do Sistema */}
        <div className="info-card">
          <div className="card-header">
            <FaDesktop />
            <h3>Sistema</h3>
          </div>
          <div className="card-content">
            <div className="info-item">
              <label>Hostname</label>
              <span>{serverData.system.hostname}</span>
            </div>
            <div className="info-item">
              <label>Plataforma</label>
              <span>{serverData.system.platform} ({serverData.system.arch})</span>
            </div>
            <div className="info-item">
              <label>Uptime</label>
              <span>{serverData.system.uptime}</span>
            </div>
            <div className="info-item">
              <label>Java</label>
              <span>{serverData.system.javaVersion}</span>
            </div>
            {serverData.system.workingDir && (
              <div className="info-item">
                <label>Diretório</label>
                <span className="path-text">{serverData.system.workingDir}</span>
              </div>
            )}
          </div>
        </div>

        {/* CPU */}
        <div className="info-card">
          <div className="card-header">
            <FaMicrochip />
            <h3>CPU</h3>
          </div>
          <div className="card-content">
            <div className="metric-large">
              <span className="value">{serverData.resources.cpu.usage}%</span>
              <span className="label">Uso Atual</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{
                  width: `${serverData.resources.cpu.usage}%`,
                  backgroundColor: getProgressColor(serverData.resources.cpu.usage)
                }}
              ></div>
            </div>
            <div className="info-item">
              <label>Detalhes</label>
              <span>{serverData.resources.cpu.cores} cores • Arquitetura: {serverData.resources.cpu.architecture}</span>
            </div>
          </div>
        </div>

        {/* Memória */}
        <div className="info-card">
          <div className="card-header">
            <FaMemory />
            <h3>Memória</h3>
          </div>
          <div className="card-content">
            <div className="metric-large">
              <span className="value">{serverData.resources.memory.used}GB / {serverData.resources.memory.total}GB</span>
              <span className="label">Uso ({serverData.resources.memory.usage}%)</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{
                  width: `${serverData.resources.memory.usage}%`,
                  backgroundColor: getProgressColor(serverData.resources.memory.usage)
                }}
              ></div>
            </div>
            <div className="info-item">
              <label>Detalhes</label>
              <span>Livre: {serverData.resources.memory.free}GB • Máx: {serverData.resources.memory.max}GB</span>
            </div>
            {serverData.resources.memory.heapUsed && (
              <div className="info-item">
                <label>Heap</label>
                <span>{serverData.resources.memory.heapUsed}MB / {serverData.resources.memory.heapMax}MB</span>
              </div>
            )}
          </div>
        </div>

        {/* Disco */}
        <div className="info-card">
          <div className="card-header">
            <FaHdd />
            <h3>Armazenamento</h3>
          </div>
          <div className="card-content">
            <div className="metric-large">
              <span className="value">{serverData.resources.disk.used}GB / {serverData.resources.disk.total}GB</span>
              <span className="label">Uso ({serverData.resources.disk.usage}%)</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{
                  width: `${serverData.resources.disk.usage}%`,
                  backgroundColor: getProgressColor(serverData.resources.disk.usage)
                }}
              ></div>
            </div>
            <div className="info-item">
              <label>Detalhes</label>
              <span>Livre: {serverData.resources.disk.free}GB • Path: {serverData.resources.disk.path}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Aplicações */}
      <div className="applications-section">
        <div className="section-header">
          <FaCogs />
          <h2>Aplicações em Execução</h2>
        </div>
        <div className="applications-table">
          <table>
            <thead>
              <tr>
                <th>Aplicação</th>
                <th>Versão</th>
                <th>Status</th>
                <th>Uptime</th>
                <th>Memória (MB)</th>
                <th>CPU (%)</th>
                <th>PID</th>
                <th>Ambiente</th>
              </tr>
            </thead>
            <tbody>
              {serverData.applications.map((app, index) => (
                <tr key={index}>
                  <td>{app.name}</td>
                  <td>
                    <span className="version-badge">{app.version}</span>
                  </td>
                  <td>
                    <div className="status-cell">
                      {getStatusIcon(app.status)}
                      <span 
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(app.status) }}
                      >
                        {app.status}
                      </span>
                    </div>
                  </td>
                  <td>{app.uptime}</td>
                  <td>{app.memory}</td>
                  <td>{app.cpu}</td>
                  <td>
                    <span className="pid-badge">{app.pid || 'N/A'}</span>
                  </td>
                  <td>
                    <span className="env-badge">{app.environment || 'default'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Métricas do Tomcat */}
      {serverData.network.tomcat && (
        <div className="section">
          <div className="section-header">
            <FaServer />
            <h2>Métricas do Tomcat</h2>
          </div>
          <div className="tomcat-grid">
            {/* Conexões e Threads */}
            <div className="info-card">
              <div className="card-header">
                <FaNetworkWired />
                <h3>Conexões e Threads</h3>
              </div>
              <div className="card-content">
                <div className="metric-large">
                  <span className="value">{serverData.network.tomcat.activeConnections || 0}</span>
                  <span className="label">Conexões Ativas</span>
                </div>
                <div className="info-item">
                  <label>Threads Atuais</label>
                  <span>{serverData.network.tomcat.currentThreads || 0} / {serverData.network.tomcat.maxThreads || 0}</span>
                </div>
                {serverData.network.tomcat.threadPoolUsage && (
                  <div className="info-item">
                    <label>Uso de Threads</label>
                    <span>{serverData.network.tomcat.threadPoolUsage}%</span>
                    <div className="progress-bar small">
                      <div 
                        className="progress-fill"
                        style={{
                          width: `${serverData.network.tomcat.threadPoolUsage}%`,
                          backgroundColor: getThreadUsageColor(serverData.network.tomcat.threadPoolUsage)
                        }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Estatísticas de Requests */}
            <div className="info-card">
              <div className="card-header">
                <FaChartLine />
                <h3>Requisições</h3>
              </div>
              <div className="card-content">
                <div className="metric-large">
                  <span className="value">{serverData.network.tomcat.totalRequests || 0}</span>
                  <span className="label">Total de Requests</span>
                </div>
                <div className="info-item">
                  <label>Requisições/min</label>
                  <span>{serverData.network.requestsPerMinute || 0}</span>
                </div>
                <div className="info-item">
                  <label>Erros</label>
                  <span>{serverData.network.tomcat.errorCount || 0}</span>
                </div>
                <div className="info-item">
                  <label>Taxa de Sucesso</label>
                  <span className="success-rate">{serverData.network.tomcat.successRate || 100}%</span>
                </div>
              </div>
            </div>

            {/* Performance */}
            <div className="info-card">
              <div className="card-header">
                <FaClock />
                <h3>Performance</h3>
              </div>
              <div className="card-content">
                <div className="metric-large">
                  <span className="value">{serverData.network.responseTime || 'N/A'}</span>
                  <span className="label">Tempo de Resposta</span>
                </div>
                {serverData.network.tomcat.maxRequestTime && (
                  <div className="info-item">
                    <label>Maior Request</label>
                    <span>{serverData.network.tomcat.maxRequestTime}ms</span>
                  </div>
                )}
                <div className="info-item">
                  <label>Tempo Total Process.</label>
                  <span>{serverData.network.tomcat.totalProcessingTime ? formatFileSize(serverData.network.tomcat.totalProcessingTime) : 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Tráfego */}
            <div className="info-card">
              <div className="card-header">
                <FaExchangeAlt />
                <h3>Tráfego de Rede</h3>
              </div>
              <div className="card-content">
                <div className="traffic-stats">
                  <div className="traffic-item upload">
                    <FaNetworkWired className="traffic-icon" />
                    <div className="traffic-info">
                      <span className="traffic-value">
                        {formatFileSize(serverData.network.tomcat.bytesSent || 0)}
                      </span>
                      <span className="traffic-label">Enviados</span>
                    </div>
                  </div>
                  <div className="traffic-item download">
                    <FaDatabase className="traffic-icon" />
                    <div className="traffic-info">
                      <span className="traffic-value">
                        {formatFileSize(serverData.network.tomcat.bytesReceived || 0)}
                      </span>
                      <span className="traffic-label">Recebidos</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rede e Estatísticas (mantido para compatibilidade) */}
      <div className="network-grid">
        <div className="stats-card">
          <div className="stats-header">
            <FaNetworkWired />
            <h3>Endereço IP</h3>
          </div>
          <div className="stats-value primary">{serverData.network.hostAddress}</div>
          <div className="stats-label">{serverData.network.hostName}</div>
        </div>

        <div className="stats-card">
          <div className="stats-header">
            <FaNetworkWired />
            <h3>Conexões Ativas</h3>
          </div>
          <div className="stats-value secondary">{serverData.network.activeConnections || 0}</div>
          <div className="stats-label">Conexões simultâneas</div>
        </div>

        <div className="stats-card">
          <div className="stats-header">
            <FaServer />
            <h3>Requisições/min</h3>
          </div>
          <div className="stats-value info">{serverData.network.requestsPerMinute || 0}</div>
          <div className="stats-label">Média do último minuto</div>
        </div>

        <div className="stats-card">
          <div className="stats-header">
            <FaHeart />
            <h3>Tempo de Resposta</h3>
          </div>
          <div className="stats-value success">{serverData.network.responseTime || 'N/A'}</div>
          <div className="stats-label">Latência média</div>
        </div>
      </div>
    </div>
  );
};

export default ServerInfo;